import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  type Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { CornerDownRight, Send } from "lucide-react";
import { StepComment } from "../dtos";
import { fieldInputSx, primaryButtonSx } from "./field-shell";
import {
  NoteAttachmentComposer,
  NoteAttachmentList,
  useNoteAttachmentDraft,
} from "./note-attachments";

/**
 * §Comments (product ask, 2026-08-06): the WM Tasks UI's per-step comment thread, ported —
 * every comment (rework reasons on the warm ground, ordinary notes on the quiet one) plus an
 * "Add a comment…" input while the agent can still write the step. The red "Why this came back"
 * alert stays a separate surface (DecisionPanel); this is the full history it summarizes.
 */
const when = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
};

const byDate = (a: StepComment, b: StepComment): number =>
  new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();

/** §Escalate-overdue: the ground a comment sits on — escalation outranks rework, rework outranks a plain note. */
const commentGround = (
  entry: StepComment,
  colors: Theme["nawy"]["colors"]["semantic"]
) => {
  if (entry.isEscalationComment) return colors.layer.extension.negativeLight;
  if (entry.isReworkComment) return colors.layer.extension.warningLight;
  return colors.layer.secondary;
};

/**
 * One comment. Module-level rather than declared inside the panel's render: a component defined during
 * render is a NEW type on every keystroke in the draft field, so React unmounts and remounts every
 * comment in the thread instead of updating it.
 */
const CommentEntry = ({
  entry,
  isReply,
  editable,
  replyingTo,
  onToggleReply,
}: {
  entry: StepComment;
  isReply: boolean;
  editable: boolean;
  replyingTo: string | null;
  onToggleReply: (uid: string) => void;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors.semantic;

  return (
    <Box
      sx={{
        p: 1,
        borderRadius: theme.nawy.radius.md,
        // A reply is indented and hangs off its root's left rule, so a thread reads as one exchange
        // rather than as unrelated notes that happen to sit next to each other.
        ml: isReply ? 2 : 0,
        borderLeft: isReply ? `2px solid ${colors.border.secondary}` : undefined,
        backgroundColor: commentGround(entry, colors),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: colors.text.primary }}>
          {entry.actorName || entry.actorType}
        </Typography>
        <Typography sx={{ fontSize: 11, color: colors.text.tertiary }}>
          {when(entry.createdAt)}
        </Typography>
        {entry.isEscalationComment && (
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: colors.text.extension.negative }}>
            Escalation
          </Typography>
        )}
      </Stack>
      <Typography sx={{ fontSize: 13, lineHeight: "19px", color: colors.text.primary }}>
        {entry.content}
      </Typography>
      <NoteAttachmentList attachments={entry.attachments} />
      {/* Reply is offered on an ESCALATION root only. An ordinary note has no counterparty to notify,
          so a reply there would be an indented comment and nothing more. */}
      {entry.isEscalationComment && !isReply && editable && (
        <Button
          size="small"
          startIcon={<CornerDownRight size={12} />}
          onClick={() => onToggleReply(entry.uid)}
          sx={{
            mt: 0.5,
            minWidth: 0,
            p: 0,
            fontSize: 11,
            fontWeight: 600,
            textTransform: "none",
            color: colors.text.extension.negative,
            "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
          }}
        >
          {replyingTo === entry.uid ? "Cancel reply" : "Reply"}
        </Button>
      )}
    </Box>
  );
};

/**
 * §Escalations-filter: what the panel shows, and whether it may offer a free composer.
 *
 * Module-level because the three answers are one rule read together, and because the alternative is four
 * more boolean operators inside a component that already carries the thread grouping.
 *
 * ⚠ Filtering happens on ROOTS, never on the flat list. A reply carries no `isEscalationComment` of its own
 * — only the root does (the server flags the escalation; a reply is an ordinary comment pointed at it), so a
 * flat `filter(isEscalationComment)` would show every escalation with its answers stripped off, which is the
 * half of the conversation nobody needs.
 */
const visibleThread = ({
  all,
  replyOnly,
  escalationsOnly,
}: {
  all: StepComment[];
  replyOnly: boolean;
  escalationsOnly: boolean;
}): { roots: StepComment[]; filterable: boolean; composerNeedsReply: boolean } => {
  /**
   * §Escalate-overdue: single-level threading. Every reply is grouped under its root and the roots keep
   * the flat chronological order the panel has always had, so a step with no escalation renders exactly
   * as before. A reply whose parent is not in this payload is deliberately promoted to a root rather
   * than dropped — losing a manager's answer because its parent was filtered out is the worse failure.
   */
  const roots = [...all]
    .filter((c) => !c.parentUid || !all.some((p) => p.uid === c.parentUid))
    .sort(byDate);
  const hasEscalation = roots.some((c) => c.isEscalationComment);
  const escalationsShown = replyOnly || (hasEscalation && escalationsOnly);
  return {
    roots: escalationsShown ? roots.filter((c) => c.isEscalationComment) : roots,
    // The control exists only where there is something to filter TO — a step nobody escalated is unchanged,
    // and the escalation-only surface is already filtered by the server, so neither grows a dead toggle.
    filterable: !replyOnly && hasEscalation,
    // No free "Add a comment…" while filtered: a root note posted from this view would vanish from it the
    // moment it saved. The one thing worth writing here is an answer to an escalation.
    composerNeedsReply: escalationsShown,
  };
};

const CommentsPanel = ({
  comments,
  caseUid,
  stepUid,
  editable,
  busy,
  onPost,
  title = "Comments",
  replyOnly = false,
}: {
  comments: StepComment[] | undefined;
  caseUid: string;
  stepUid: string;
  /** input renders only while the agent may write the step (WM parity) */
  editable: boolean;
  busy: boolean;
  /** §Escalate-overdue: `parentUid` makes the post a reply on that comment's thread */
  onPost: (content: string, parentUid?: string, attachmentUids?: string[]) => Promise<unknown>;
  /** §Escalate-thread: the escalation-only surface names itself, since it is not the full history. */
  title?: string;
  /**
   * §Escalate-thread: the composer appears only while replying to an escalation — there is no free
   * "Add a comment…" box. On a step the agent cannot open, a root note would be a comment nobody is
   * notified about, on a thread they cannot see the rest of; the one thing worth writing there is an
   * answer to the escalation. Reply is still gated on `editable`, which carries the server's `canReply`.
   */
  replyOnly?: boolean;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors.semantic;
  const [draft, setDraft] = useState("");
  /** §Escalate-overdue: the escalation being replied to, or null for an ordinary note. */
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  /** §Escalations-filter: off by default — an untouched panel reads exactly as it always has. */
  const [escalationsOnly, setEscalationsOnly] = useState(false);
  const attachmentContext = replyingTo
    ? {kind: "escalation-reply" as const, stepUid, threadUid: replyingTo}
    : {kind: "comment" as const, stepUid};
  const attachmentDraft = useNoteAttachmentDraft({caseUid, context: attachmentContext});

  const all = comments ?? [];
  // An empty thread renders nothing. `replyOnly` has no free composer to offer, so with nothing to reply
  // to there is no panel — otherwise a step whose escalation was never raised would grow a bare heading.
  if (!all.length && (!editable || replyOnly)) return null;

  const {roots, filterable, composerNeedsReply} = visibleThread({
    all,
    replyOnly: Boolean(replyOnly),
    escalationsOnly,
  });
  // Replies are read off the FULL list, not the filtered roots — an answer belongs to its escalation
  // whether or not the panel is currently filtered.
  const repliesOf = (uid: string): StepComment[] =>
    all.filter((c) => c.parentUid === uid).sort(byDate);

  const post = () => {
    const content = draft.trim();
    if (
      busy ||
      attachmentDraft.pending ||
      attachmentDraft.hasErrors ||
      (!content && !attachmentDraft.attachmentUids.length)
    ) return;
    void onPost(content, replyingTo ?? undefined, attachmentDraft.attachmentUids).then(() => {
      attachmentDraft.markSubmitted();
      setDraft("");
      setReplyingTo(null);
    }, () => undefined);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${colors.border.secondary}`,
        borderRadius: theme.nawy.radius.lg,
        p: 1.5,
        mt: 2,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: roots.length || editable ? 1 : 0 }}
      >
        <Typography
          sx={{
            fontSize: 12,
            lineHeight: "18px",
            fontWeight: 600,
            color: colors.text.primary,
          }}
        >
          {escalationsOnly ? "Escalations" : title}
        </Typography>
        {/* §Escalations-filter: one button that names what it will DO, not the state it is in — a segmented
            "All | Escalations" control costs a row of chrome on every step to answer a question most steps
            never raise. It appears only where the step actually carries an escalation, so an ordinary
            thread is untouched. */}
        {filterable && (
          <Button
            size="small"
            disabled={busy}
            onClick={() => {
              if (busy) return;
              setEscalationsOnly(!escalationsOnly);
              // Leaving the filtered view with a reply half-aimed would leave the composer pointed at a
              // thread that may no longer be on screen.
              setReplyingTo(null);
            }}
            sx={{
              minWidth: 0,
              p: 0,
              fontSize: 11,
              fontWeight: 600,
              textTransform: "none",
              flexShrink: 0,
              color: escalationsOnly
                ? colors.text.secondary
                : colors.text.extension.negative,
              "&:hover": {
                backgroundColor: "transparent",
                textDecoration: "underline",
              },
            }}
          >
            {escalationsOnly ? "Show all comments" : "Escalations only"}
          </Button>
        )}
      </Stack>
      <Stack spacing={0.75}>
        {roots.map((entry) => (
          <Stack key={entry.uid} spacing={0.5}>
            <CommentEntry
              entry={entry}
              isReply={false}
              editable={editable && !busy}
              replyingTo={replyingTo}
              onToggleReply={(uid) => setReplyingTo(replyingTo === uid ? null : uid)}
            />
            {repliesOf(entry.uid).map((reply) => (
              <CommentEntry
                key={reply.uid}
                entry={reply}
                isReply
                editable={editable && !busy}
                replyingTo={replyingTo}
                onToggleReply={(uid) => setReplyingTo(replyingTo === uid ? null : uid)}
              />
            ))}
          </Stack>
        ))}
      </Stack>
      {editable && (!composerNeedsReply || replyingTo) && (
        <Stack spacing={1} sx={{ mt: roots.length ? 1 : 0 }}>
          <Stack direction="row" spacing={1}>
          <TextField
            fullWidth
            size="small"
            placeholder={replyingTo ? "Reply to the escalation…" : "Add a comment…"}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                post();
              }
            }}
            sx={fieldInputSx(theme)}
          />
          <Button
            variant="contained"
            disabled={
              busy ||
              attachmentDraft.pending ||
              attachmentDraft.hasErrors ||
              (!draft.trim() && !attachmentDraft.attachmentUids.length)
            }
            startIcon={busy ? <CircularProgress size={13} /> : <Send size={13} />}
            onClick={post}
            // §UI (product ask, 2026-08-06): Post matches the field's height exactly
            sx={{ ...primaryButtonSx(theme), height: 36, flexShrink: 0 }}
          >
            {replyingTo ? "Reply" : "Post"}
          </Button>
          </Stack>
          <NoteAttachmentComposer draft={attachmentDraft} disabled={busy} />
        </Stack>
      )}
    </Paper>
  );
};

export default CommentsPanel;
